import React, { useEffect } from 'react';
import { StationeryTemplate } from '../../../DesignsGallery.types';
import { GalleryTile, GalleryTileWithRef, TagVariant } from '../../../../GalleryTile';
import { templateToTitle } from '@apps/card/utils';
import { maybeApplyCallback } from '@shared/utils/maybeApplyCallback';
import { Box, TextV2 } from '@withjoy/joykit';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';
import { useAuth } from '@shared/components/AuthProvider';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { desktopAspect, mobileAspect, tileRowGap } from '../../CardsConfig';
import { CardTileCarousel } from './CardTileCarousel/CardTileCarousel';
import { CardCarouselSlide } from '../hooks/useCardCarouselValues';
import { ProductPrices } from './ProductPrices/ProductPrices';
import { CardFormat } from '../../../../ProductDetailsPage/constants';
import { useItemImpressionsTelemetry } from '../../ItemImpressionsTelemetry';
import { useInView } from '@shared/hooks/useInView';
import { CardThumbnail } from './CardThumbnail';

export type CardTileSource = 'shop' | 'favoritesGallery';

type CardTileProps = {
  data: StationeryTemplate & { type: 'template'; matchingTagCount?: number };
  eventId?: string;
  eventDesignThemeId?: string;
  onFavoriteClick?: (template: StationeryTemplate) => Promise<void>;
  hideTag?: boolean;
  cardTileSource: CardTileSource;
  tagCount?: number;
  showSearchDebugInfo?: boolean;
  disableAssetRotationOnHover: boolean;
  cardCarouselSlides?: CardCarouselSlide[];
  enableFreePriceString?: boolean;
  preferredFormat?: CardFormat;
};

export const CardTile = (props: CardTileProps) => {
  const { isSuperAdmin } = useAuth();
  const { t } = useTranslation('stationery');
  const isMobile = useIsMobileScreen();

  const { value: printDesignGalleryTileAssetRotationOnHoverExperiment } = useFeatureValue('printDesignGalleryTileAssetRotationOnHoverExperiment', {
    skip: props.disableAssetRotationOnHover,
  });
  const isAssetRotationOnHoverEnabled = printDesignGalleryTileAssetRotationOnHoverExperiment === 'treatment';

  const [isAutofillEnabled] = useIsFeatureEnabled('printGalleryAutofill');

  const translations = t('dashboard', 'designsGallery');
  const { cardTileSource, data, eventDesignThemeId, hideTag, cardCarouselSlides, tagCount, showSearchDebugInfo, preferredFormat } = props;
  // enableFreePriceString ^^^
  const { category, approved, format, thumbnailUrl, enabled, digitalDesignThumbnailUrl } = data;

  const { designGalleryTileClicked, markTemplateAsFavorite } = useCardTelemetry();

  const { goToProductDetailsPage } = useCardsRouterContext();

  const handleOnViewTemplatePdpClick = useEventCallback((template: StationeryTemplate) => {
    designGalleryTileClicked({ themeId: template.themeId, templateCategory: template.category, format: template.format, isPremiumDesign: template.premium });
    goToProductDetailsPage('wedding', category, template.themeId, template.id, 'push', preferredFormat);
  });

  const isPremium = data.premium || format === 'paper';

  const tagVariant: TagVariant | undefined = enabled
    ? approved
      ? // No tag if enabled
        undefined
      : 'comingSoon'
    : 'disabled';

  const priceElement = <ProductPrices template={data} />;

  const handleOnToggleFavorite = useEventCallback((isFavorite: boolean) => {
    markTemplateAsFavorite({ themeId: data.themeId, templateCategory: data.category, isFavorite, source: cardTileSource });
  });

  const { setImpression } = useItemImpressionsTelemetry();

  // Threshold set to 0.6 because:
  // a) When scrolling up, the image of the tile is fully revealed by 0.6. No need to wait until 1.0.
  // b) If 1.0, the right edge of tiles isn't counting as "in view". Perhaps we're off by a pixel in layout.
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.6 });
  useEffect(() => {
    if (inView) setImpression(data.id);
  }, [data.id, setImpression, inView]);

  const badges = (
    <>
      {isPremium && <GalleryTile.PremiumBadge />}
      <GalleryTile.FavoriteBadge
        eventId={props.eventId}
        onToggle={handleOnToggleFavorite}
        template={data}
      />
      {data.isFoil && <GalleryTile.FoilBadge />}
    </>
  );

  return enabled || isSuperAdmin ? (
    <GalleryTileWithRef
      ref={ref}
      aspectRatio={isMobile ? mobileAspect : desktopAspect}
      alignContent="start"
      rowGap={tileRowGap}
      onClick={maybeApplyCallback(!!(approved || isSuperAdmin), () => handleOnViewTemplatePdpClick(data))}
    >
      {eventDesignThemeId === data.themeId && (
        <Box
          typographyVariant="label1"
          fontWeight={600}
          position="absolute"
          paddingX={4}
          paddingY={2}
          left={5}
          top={4}
          backgroundColor="mono14"
          color="white"
          userSelect="none"
          borderRadius={5}
          zIndex={1}
        >
          {translations.websiteMatch()}
        </Box>
      )}
      {(() => {
        if (isAssetRotationOnHoverEnabled && cardCarouselSlides) {
          return (
            <CardTileCarousel
              data={data}
              slides={cardCarouselSlides}
            >
              {badges}
            </CardTileCarousel>
          );
        }
        if (isAutofillEnabled) {
          return <CardThumbnail template={data}>{badges}</CardThumbnail>;
        }
        return <GalleryTile.AssetPreview assetUrl={(thumbnailUrl || digitalDesignThumbnailUrl) ?? ''}>{badges}</GalleryTile.AssetPreview>;
      })()}

      {showSearchDebugInfo && tagCount && <TextV2 textAlign="center">{`${data.matchingTagCount ?? '0'}/${tagCount ?? 0}`}</TextV2>}
      <GalleryTile.Content
        title={templateToTitle(data)}
        label={priceElement}
      >
        {!hideTag && tagVariant && <GalleryTile.Tag variant={tagVariant} />}
      </GalleryTile.Content>
    </GalleryTileWithRef>
  ) : null;
};
